import * as React from "react";
import { useNavigate } from "react-router";

import Box from "@mui/material/Box";

import type {
    HomePromptDTO,
    HomePromptOption,
    RecommendedContent,
} from "@volley/data";

import { useCurrentUser } from "../hooks/currentUser";
import useIntercom from "../hooks/useIntercom";

import ChatHeader from "./ChatHeader";
import Composer from "./Composer";
import { contentNavUrl } from "./ContentResultCard";
import ThreadMessageView from "./ThreadMessageView";
import { messagesForResult, resultEventMetadata } from "./answers";
import {
    SURFACE,
    closeConversation,
    escalateConversation,
    fetchFreeTextRecommendations,
    fetchRecommendations,
    rateAsk,
    recordPromptEvent,
    submitProfileAnswer,
} from "./api";
import { saveChatSession } from "./chatSession";
import {
    BROWSE_ALL_VALUE,
    isAssessmentLaunchOption,
    isAptaConnectOption,
    isDuprConnectOption,
    isSeeStatsOption,
    WORKOUTS_SEARCH_ROUTE,
} from "./constants";
import { toTurns } from "./thread";
import type { ThreadMessage } from "./types";

const NAVY_DEEP = "#0B1730";

/**
 * Cap on recommended content shown for an answer. Kept small so the carousel
 * stays a focused pick, not a full list — and so the recommendation snapshot
 * (what the server records as "served") matches what the user actually saw.
 */
const MAX_RECOMMENDATIONS = 4;

/**
 * How the chat opens when nothing prompted it.
 *
 * Deliberately an invitation rather than a greeting with no exit: opened from
 * the launcher there are no chips to tap, so the only way forward is the
 * composer, and the opening line has to say so.
 */
const LAUNCHER_GREETING =
    "Ask me anything — about your training, or about using your trainer.";

/**
 * How the chat re-opens after "New chat".
 *
 * Never the prompt's own words, even when there is a prompt: the person has
 * just said they are done with what we suggested, and handing them the same
 * suggestion back reads as not having listened. An open question instead.
 */
const NEW_CHAT_GREETING = "What can I help you with today?";

/**
 * What the header says the chat is doing.
 *
 * "Picked for you" is the promise a home-screen prompt made, and it holds only
 * while the thread still answers that prompt. After "New chat" nothing has been
 * picked, and claiming otherwise over an open question is a lie the user can
 * see — so a started-over chat reads like one opened from the launcher.
 */
function headerStatus(hasPrompt: boolean, startedOver: boolean): string {
    return hasPrompt && !startedOver ? "Picked for you" : "Ask anything";
}

/** The three ways a chat can begin. Pure, so it can be read in one sitting. */
function seedThread({
    restored,
    freeTextSeed,
    prompt,
}: Pick<
    AskVolleyOverlayProps,
    "restored" | "freeTextSeed" | "prompt"
>): ThreadMessage[] {
    // Restoring a chat the user left to view a workout: seed from the saved
    // thread verbatim (nothing is re-fetched).
    if (restored) return restored.thread;

    // Opened from the widget's open field: lead with their own message and an
    // acknowledgement, because the question is already asked.
    if (freeTextSeed) {
        return [
            { kind: "user", content: freeTextSeed },
            {
                kind: "assistant",
                content: "Got it — let me pull that together for you.",
            },
        ];
    }

    // Otherwise we speak first: the prompt's own words, or the invitation when
    // nothing prompted this.
    return [
        { kind: "assistant", content: prompt?.promptText ?? LAUNCHER_GREETING },
    ];
}

/**
 * Options that leave the chat rather than being answered in it — start an
 * assessment, link a rating, open the dashboard. Returns true when the option
 * was one of them and has been played.
 *
 * Grouped because they are one idea (an option that is a launch, not a
 * question) and because inlining four of them made the one function that
 * actually answers a question hard to read past.
 */
function playLaunchOption(
    option: HomePromptOption,
    handlers: Pick<
        AskVolleyOverlayProps,
        "onLaunchAssessment" | "onConnectDupr" | "onConnectApta" | "onSeeStats"
    >,
): boolean {
    // "Start" / "Finish assessment": drives the assessment flow directly.
    if (isAssessmentLaunchOption(option)) {
        handlers.onLaunchAssessment(option);
        return true;
    }
    // "Connect DUPR": opens the existing DUPR connect flow.
    if (isDuprConnectOption(option)) {
        handlers.onConnectDupr(option);
        return true;
    }
    // Platform tennis's equivalent: link an APTA id so PTI resolves.
    if (isAptaConnectOption(option)) {
        handlers.onConnectApta(option);
        return true;
    }
    // "See my stats": navigates to the performance dashboard.
    if (isSeeStatsOption(option)) {
        handlers.onSeeStats(option);
        return true;
    }
    return false;
}

interface AskVolleyOverlayProps {
    /**
     * The prompt this chat answers. Absent when Ask Volley was opened on its
     * own: there is no prompt to seed the thread with, no options to play, and
     * nothing for an event to be attributed to.
     */
    prompt?: HomePromptDTO;
    impressionUlid: string;
    /** Where this chat is being held; tags every event it records. */
    surface?: string;
    /** The user's currently-selected sport; locks recommendations to it. */
    sportName?: string;
    /** Id of that same sport, recorded as the funnel dimension on every event. */
    sportId?: number | null;
    /** An option chosen in the widget, played automatically on open. */
    initialOptionValue?: string;
    /** Free text from the widget's open field; seeds the thread when present. */
    freeTextSeed?: string;
    /**
     * A previously-open chat to restore (after the user backed out of a workout
     * detail page). When present, the thread and remaining options are seeded
     * from it and the play-once effects are suppressed, so nothing is re-fetched.
     */
    restored?: {
        thread: ThreadMessage[];
        /**
         * The conversation the restored thread belongs to. Without it a
         * restored chat would keep asking, but every answer would open a new
         * conversation beside the one already holding its earlier turns.
         */
        conversationUlid?: string;
    };
    onClose: () => void;
    /** Drive the assessment flow (start/resume) instead of fetching content. */
    onLaunchAssessment: (option: HomePromptOption) => void;
    /** Open the DUPR connect flow (profile prompt) instead of fetching content. */
    onConnectDupr: (option: HomePromptOption) => void;
    /** Open the APTA-id dialog (platform tennis's rating link) instead of content. */
    onConnectApta: (option: HomePromptOption) => void;
    /** Navigate to the performance dashboard ("see my stats") instead of content. */
    onSeeStats: (option: HomePromptOption) => void;
}

/**
 * Fullscreen Ask Volley chat overlay. Seeds the thread with the prompt, lets the
 * user pick an answer, then shows the routed content recommendations (content
 * prompts) or acknowledges the answer (profile prompts).
 */
export default function AskVolleyOverlay({
    prompt,
    impressionUlid,
    surface = SURFACE,
    sportName,
    sportId,
    initialOptionValue,
    freeTextSeed,
    restored,
    onClose,
    onLaunchAssessment,
    onConnectDupr,
    onConnectApta,
    onSeeStats,
}: AskVolleyOverlayProps): React.JSX.Element {
    const navigate = useNavigate();
    const { currentUser } = useCurrentUser();
    const intercom = useIntercom();
    const userId = currentUser?.id;
    const [thread, setThread] = React.useState<ThreadMessage[]>(() =>
        seedThread({ restored, freeTextSeed, prompt }),
    );
    const [busy, setBusy] = React.useState(false);
    const [mounted, setMounted] = React.useState(false);
    const [draft, setDraft] = React.useState("");
    // Set once "New chat" has run. The prompt still exists as a prop, but the
    // thread no longer answers it — so the header must stop saying so.
    const [startedOver, setStartedOver] = React.useState(false);
    // The server-side conversation these turns belong to. The server mints it on
    // the first ask and hands it back; every later ask echoes it so the turns
    // land together. Undefined until the first answer returns, and again after a
    // "New chat" — in both cases the next ask opens a fresh one.
    const [conversationUlid, setConversationUlid] = React.useState<
        string | undefined
    >(restored?.conversationUlid);
    const threadRef = React.useRef<HTMLDivElement>(null);
    const composerRef = React.useRef<HTMLInputElement>(null);

    // Drive the slide-in on the next frame.
    React.useEffect(() => {
        const id = requestAnimationFrame(() => setMounted(true));
        return () => cancelAnimationFrame(id);
    }, []);

    // Keep the thread pinned to the latest message.
    React.useEffect(() => {
        if (threadRef.current) {
            threadRef.current.scrollTop = threadRef.current.scrollHeight;
        }
    }, [thread]);

    // Leave the chat for the full workouts library via the fixed trailing
    // carousel card, capturing the choice for attribution.
    const handleBrowseAll = React.useCallback(() => {
        recordPromptEvent({
            eventType: "interaction",
            prompt,
            impressionUlid,
            sportId,
            surface,
            metadata: {
                action: "browse_all",
                answerValue: BROWSE_ALL_VALUE,
                answerLabel: "Browse all",
                via: "carousel",
            },
        });
        // Leaving via the trailing carousel card keeps the conversation going, so
        // persist it (like tapping a workout) — backing out of the library then
        // restores this exact thread and its recommendations.
        saveChatSession(userId, {
            savedAt: Date.now(),
            impressionUlid,
            sportId,
            prompt,
            surface,
            initialOptionValue,
            freeTextSeed,
            thread,
            conversationUlid,
        });
        onClose();
        navigate(WORKOUTS_SEARCH_ROUTE);
    }, [
        impressionUlid,
        sportId,
        navigate,
        onClose,
        prompt,
        userId,
        initialOptionValue,
        freeTextSeed,
        thread,
        surface,
        conversationUlid,
    ]);

    const handleSelect = React.useCallback(
        async (option: HomePromptOption) => {
            // Options only ever come off a prompt, so there is nothing to
            // select without one. Stated rather than assumed: it is also what
            // keeps the prompt reads below narrowed.
            if (busy || !prompt) return;

            // Options that leave the chat instead of being answered in it. The
            // parent records the interaction in each case.
            const launched = playLaunchOption(option, {
                onLaunchAssessment,
                onConnectDupr,
                onConnectApta,
                onSeeStats,
            });
            if (launched) return;

            // A dismiss option ("Maybe later") closes the chat instead of
            // routing or saving an answer; the choice is still captured.
            if (option.dismiss) {
                recordPromptEvent({
                    eventType: "interaction",
                    prompt,
                    impressionUlid,
                    sportId,
                    surface,
                    metadata: {
                        action: "dismiss",
                        answerValue: option.value,
                        answerLabel: option.label,
                    },
                });
                onClose();
                return;
            }

            setBusy(true);
            recordPromptEvent({
                eventType: "interaction",
                prompt,
                impressionUlid,
                sportId,
                surface,
                metadata: {
                    action: "answer_select",
                    answerValue: option.value,
                    answerLabel: option.label,
                },
            });
            setThread((prev) => [
                ...prev,
                { kind: "user", content: option.label },
            ]);

            if (prompt.promptType === "profile") {
                await submitProfileAnswer(
                    prompt.id,
                    { value: option.value },
                    impressionUlid,
                );
                setThread((prev) => [
                    ...prev,
                    {
                        kind: "assistant",
                        content:
                            "Got it — I'll use that to tune your recommendations.",
                    },
                ]);
                setBusy(false);
                return;
            }

            setThread((prev) => [...prev, { kind: "loading" }]);
            const cards = await fetchRecommendations(
                {
                    homePromptId: prompt.id,
                    answerValue: option.value,
                    impressionUlid,
                    ...(sportName ? { sportName } : {}),
                },
                MAX_RECOMMENDATIONS,
            );
            setThread((prev) => {
                const next = prev.filter((m) => m.kind !== "loading");
                if (cards.length === 0) {
                    return [
                        ...next,
                        {
                            kind: "assistant",
                            content:
                                "I couldn't find a match for that right now — try another option.",
                        },
                    ];
                }
                return [
                    ...next,
                    {
                        kind: "assistant",
                        content: "Here's what I'd start with:",
                    },
                    { kind: "carousel", cards },
                ];
            });
            setBusy(false);
        },
        [
            busy,
            impressionUlid,
            sportId,
            surface,
            prompt,
            sportName,
            onClose,
            onLaunchAssessment,
            onConnectApta,
            onConnectDupr,
            onSeeStats,
        ],
    );

    // Play the option the user tapped in the widget, once.
    // When restoring, the thread is already populated — suppress both play-once
    // effects so the option/free-text recommendations aren't re-fetched.
    const initialPlayed = React.useRef(Boolean(restored));
    React.useEffect(() => {
        if (initialPlayed.current || !initialOptionValue || !prompt) return;
        const option = prompt.options.find(
            (o) => o.value === initialOptionValue,
        );
        if (option) {
            initialPlayed.current = true;
            void handleSelect(option);
        }
    }, [initialOptionValue, prompt, handleSelect]);

    /**
     * Ask a free-text question: send it along with the conversation so far, then
     * render the answer (or a graceful miss).
     *
     * This used to live inside the play-once seed effect. The composer needs
     * exactly the same behaviour on every turn, so the seed is now just the
     * first call rather than a special case.
     *
     * `seeded` marks the one ask that is already in the thread before it is
     * sent: the overlay opens showing the question the user typed in the widget.
     * That makes it both the question and, without this, its own context — so a
     * seeded ask neither echoes the user again nor carries any prior turns.
     */
    const handleAsk = React.useCallback(
        async (text: string, { seeded = false }: { seeded?: boolean } = {}) => {
            const question = text.trim();
            if (!question || busy) return;

            // The turns as they stand BEFORE this question. `question` travels
            // as the ask itself, so including it here would hand the model its
            // own input twice, once as context for itself.
            const priorTurns = seeded ? [] : toTurns(thread);

            setBusy(true);
            setThread((prev) => [
                ...prev,
                ...(seeded
                    ? []
                    : [{ kind: "user" as const, content: question }]),
                { kind: "loading" as const },
            ]);

            const result = await fetchFreeTextRecommendations({
                text: question,
                sportName,
                impressionUlid,
                thread: priorTurns,
                conversationUlid,
            });

            // Every outcome carries the conversation, the misses included: a
            // question that failed is still a turn of the same conversation, and
            // a transcript handed to a human that skipped it would omit the very
            // thing that sent them there.
            if (result.conversationUlid) {
                setConversationUlid(result.conversationUlid);
            }

            recordPromptEvent({
                eventType: "interaction",
                prompt,
                impressionUlid,
                sportId,
                surface,
                metadata: resultEventMetadata(result),
            });
            setThread((prev) => [
                ...prev.filter((m) => m.kind !== "loading"),
                ...messagesForResult(result, question),
            ]);
            setBusy(false);
        },
        [
            busy,
            thread,
            conversationUlid,
            sportName,
            impressionUlid,
            surface,
            prompt,
            sportId,
        ],
    );

    // Answer the free-text request the user typed in the widget, once.
    const freeTextPlayed = React.useRef(Boolean(restored));
    React.useEffect(() => {
        if (freeTextPlayed.current || !freeTextSeed) return;
        freeTextPlayed.current = true;
        void handleAsk(freeTextSeed, { seeded: true });
    }, [freeTextSeed, handleAsk]);

    /** Send the composer's contents as the next turn. */
    const submitDraft = React.useCallback(() => {
        const text = draft.trim();
        if (!text || busy) return;
        setDraft("");
        void handleAsk(text);
    }, [draft, busy, handleAsk]);

    /**
     * Hand this conversation to a human.
     *
     * Tries the transcript first: an agent who can see what was already asked,
     * what we answered and which answer was rejected does not have to make
     * anyone repeat themselves. When that is unavailable — the hand-off is
     * switched off, Intercom has never seen this player, or the call failed —
     * it falls back to opening the messenger prefilled with their question,
     * which is exactly what shipped before any of this. The fallback matters
     * more than usual here: this is the moment we have already let someone
     * down once, and a dead end would be the second time.
     */
    const handleEscalate = React.useCallback(
        (origin: "help" | "unroutable" | "error", question?: string) => {
            recordPromptEvent({
                eventType: "interaction",
                prompt,
                impressionUlid,
                sportId,
                surface,
                metadata: {
                    action: "support_escalate",
                    source: "widget",
                    origin,
                },
            });

            void (async () => {
                const handedOver = conversationUlid
                    ? await escalateConversation(conversationUlid)
                    : null;
                if (handedOver) {
                    // Open the messenger onto that conversation by id. A plain
                    // show() lands on the messenger's cached view, which was
                    // built before this conversation existed and does not
                    // learn about it until a reload.
                    intercom.showConversation(handedOver);
                } else {
                    intercom.newMessage(question);
                }
            })();
        },
        [conversationUlid, intercom, prompt, impressionUlid, sportId, surface],
    );

    /**
     * Record whether an answer helped, and show the choice immediately.
     *
     * The thumb is marked locally without waiting for the write: it is a
     * one-tap gesture, and making someone watch a spinner to find out whether
     * their opinion registered is worse than losing the occasional row.
     */
    const handleRate = React.useCallback(
        (askId: number, helpful: boolean) => {
            rateAsk(askId, helpful);
            recordPromptEvent({
                eventType: "interaction",
                prompt,
                impressionUlid,
                sportId,
                surface,
                metadata: { action: "answer_rating", askId, helpful },
            });
            setThread((prev) =>
                prev.map((m) =>
                    m.kind === "rating" && m.askId === askId
                        ? { ...m, helpful }
                        : m,
                ),
            );
        },
        [prompt, impressionUlid, sportId, surface],
    );

    /**
     * The "let me try again" side of a thumb down: put the cursor in the
     * composer. We deliberately do not re-ask on the user's behalf — the whole
     * value of the retry is that it knows what was wrong, and only they can say.
     */
    const handleRetry = React.useCallback(() => {
        composerRef.current?.focus();
    }, []);

    /**
     * Start over: drop the thread on screen and close the conversation server
     * side, so its turns stop being replayed to the models.
     *
     * Needed precisely because follow-ups carry context. Without it a user who
     * moves on to an unrelated question drags the old one along with no way to
     * say otherwise.
     *
     * Re-opens on an open question rather than the prompt. Starting over is the
     * user declining what we suggested; showing it again is the wrong reply.
     */
    const handleNewChat = React.useCallback(() => {
        if (busy) return;
        if (conversationUlid) closeConversation(conversationUlid);
        recordPromptEvent({
            eventType: "interaction",
            prompt,
            impressionUlid,
            sportId,
            surface,
            metadata: { action: "new_chat" },
        });
        setConversationUlid(undefined);
        setDraft("");
        setStartedOver(true);
        setThread([{ kind: "assistant", content: NEW_CHAT_GREETING }]);
    }, [busy, conversationUlid, prompt, impressionUlid, sportId, surface]);

    const handleStart = React.useCallback(
        (content: RecommendedContent) => {
            recordPromptEvent({
                eventType: "interaction",
                prompt,
                impressionUlid,
                sportId,
                surface,
                metadata: {
                    action: "content_tap",
                    contentId: content.id,
                    contentType: content.type,
                },
            });
            // Persist the open chat before leaving, so backing out of the workout
            // detail restores this exact thread (and its other recommendations).
            saveChatSession(userId, {
                savedAt: Date.now(),
                impressionUlid,
                sportId,
                prompt,
                surface,
                initialOptionValue,
                freeTextSeed,
                thread,
                conversationUlid,
            });
            onClose();
            navigate(contentNavUrl(content), {
                state: { source: "ask_volley" },
            });
        },
        [
            impressionUlid,
            sportId,
            navigate,
            onClose,
            prompt,
            userId,
            initialOptionValue,
            freeTextSeed,
            thread,
            surface,
            conversationUlid,
        ],
    );

    return (
        <Box
            sx={{
                position: "fixed",
                inset: 0,
                zIndex: (t) => t.zIndex.modal,
                bgcolor: NAVY_DEEP,
                display: "flex",
                flexDirection: "column",
                color: "#fff",
                transform: mounted ? "translateY(0)" : "translateY(20px)",
                opacity: mounted ? 1 : 0,
                transition:
                    "transform 260ms cubic-bezier(.2,.8,.2,1), opacity 220ms ease-out",
            }}
        >
            <ChatHeader
                status={headerStatus(Boolean(prompt), startedOver)}
                canClear={thread.length > 1}
                busy={busy}
                onNewChat={handleNewChat}
                onClose={onClose}
            />

            {/* Thread */}
            <Box
                ref={threadRef}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                    py: 2,
                }}
            >
                {thread.map((message, i) => (
                    <ThreadMessageView
                        key={`${message.kind}-${i}`}
                        message={message}
                        onStart={handleStart}
                        onBrowseAll={handleBrowseAll}
                        onRate={handleRate}
                        onRetry={handleRetry}
                        onEscalate={handleEscalate}
                    />
                ))}
            </Box>

            <Composer
                inputRef={composerRef}
                value={draft}
                onChange={setDraft}
                onSubmit={submitDraft}
                busy={busy}
            />
        </Box>
    );
}
